import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FastQuoteBids } from '@app-model/evaluation';
import { DefaultStatus } from '@app-model/login';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { AlternativeBidRequest, BidResult } from './dealer.service';
import { map } from "rxjs/operators";
import { FastQuoteQuantity, FastQuoteSearch, LongFastQuoteResponse } from '@app-model/fast-quote';
import { Vehicle } from '@app-model/vehicle';
import { FilterOptions, VehicleFilter } from './open.service';
import { DirectSaleRequest, PotentialBuyerFilters, PotentialBuyerResponse } from './oem-dealer.service';

@Injectable({
  providedIn: 'root'
})
export class FastQuoteService {

  constructor(
    private http: HttpClient,
  ) { }

  sendEvalFastQuote(id: string, minPrice: number, personalizedFinishTime: string, target: string, exclusiveGroups: string[], isAuction: boolean, bidStep: number): Observable<DefaultStatus> {
    const opts = { params: {} };
    if (minPrice) {
      opts.params['minBidValue'] = '' + minPrice;
    }
    if (personalizedFinishTime) {
      opts.params['personalizedFinishTime'] = personalizedFinishTime;
    }
    if (exclusiveGroups.length > 0) {
      opts.params['dealersExclusiveGroups'] = exclusiveGroups;
    }
    if(isAuction){
      opts.params['isAuction'] = 'true';
    }
    if(bidStep){
      opts.params['bidStep'] = '' + bidStep;
    }
    opts.params['target'] = target;
    return this.http
      .post(`${environment.api}/fastQuote/management/startFromEvaluation/${id}`, null, opts)
      .pipe(map((response: any) => response));
  }
  sendTradeFastQuote(id: string, minPrice: number, personalizedFinishTime: string, target: string, exclusiveGroups: string[], isAuction: boolean, bidStep: number): Observable<DefaultStatus> {
    const opts = { params: {} };
    if (minPrice) {
      opts.params['minBidValue'] = '' + minPrice;
    }
    if (personalizedFinishTime) {
      opts.params['personalizedFinishTime'] = personalizedFinishTime;
    }
    if (exclusiveGroups.length > 0) {
      opts.params['dealersExclusiveGroups'] = exclusiveGroups;
    }
    if(isAuction){
      opts.params['isAuction'] = 'true';
    }
    if(bidStep){
      opts.params['bidStep'] = '' + bidStep;
    }
    opts.params['target'] = target;
    return this.http
      .post(`${environment.api}/fastQuote/management/startFromTradein/${id}`, null, opts)
      .pipe(map((response: any) => response));
  }

  makeCounterOffer(fastQuoteId: string, request: FastQuoteBids): Observable<BidResult> {
    return this.http
      .post(`${environment.api}/fastQuote/management/makeCounterOffer/${fastQuoteId}`, request)
      .pipe(map((response: any) => response));
  }

  acceptCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/fastQuote/acceptCounterOffer/${vehicleId}`, request)
      .pipe(map((response: BidResult) => response));
  }

  rejectCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/fastQuote/rejectCounterOffer/${vehicleId}`, request)
      .pipe(map((response: BidResult) => response));
  }

  cancelFastQuote(id): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/fastQuote/management/cancel/${id}`, null)
      .pipe(map((response: any) => response));
  }

  cancelSoldFastQuote(
    fastQuoteId: string,
    request: { cancellationReason: string; cancellationNote?: string }
  ): Observable<void> {
    return this.http
      .post(`${environment.api}/fastQuote/management/cancelSold/${fastQuoteId}`, request)
      .pipe(map(() => undefined));
  }

  acceptBidFastQuote(data: FastQuoteBids, id: string): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/fastQuote/management/acceptBidSuggestion/${id}`, data)
      .pipe(map((response: any) => response));
  }

  rejectBidFastQuote(data: FastQuoteBids, id: string): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/fastQuote/management/rejectBidSuggestion/${id}`, data)
      .pipe(map((response: any) => response));
  }

  isLongFastQuote(vehicleId: string, evalVehicle: boolean): Observable<LongFastQuoteResponse> {
    return this.http
      .get(`${environment.api}/fastQuote/longFastQuote/${vehicleId}/${evalVehicle}`)
      .pipe(map((response: any) => response));
  }
  searchFastQuotes(data: VehicleFilter): Observable<FastQuoteSearch> {
    return this.http
      .post(`${environment.api}/fastQuote/search`, data)
      .pipe(map((response: any) => response));
  }
  getFastQuoteVehicle(id): Observable<Vehicle> {
    return this.http
      .get(`${environment.api}/fastQuote/${id}`)
      .pipe(map((response: Vehicle) => Object.assign(new Vehicle(), response)));;
  }
  addBid(id, value): Observable<DefaultStatus> {
    return this.http
      .put(`${environment.api}/dealer/fastQuote/addBid/${id}/${value}`, null)
      .pipe(map((response: any) => response));
  }
  cancelBid(id): Observable<DefaultStatus> {
    return this.http
      .put(`${environment.api}/dealer/fastQuote/removeBid/${id}`, null)
      .pipe(map((response: any) => response));
  }
  getFilterOptions(filter: any): Observable<FilterOptions>{
    return this.http.post<FilterOptions>(`${environment.api}/fastQuote/filterOptions`, filter);
  }

  directSaleFromEvaluation(evalVehicleId: string, request: DirectSaleRequest): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/fastQuote/management/directSale/evaluation/${evalVehicleId}`, request)
      .pipe(map((response: DefaultStatus) => response));
  }

  directSaleFromTradeIn(tradeInVehicleId: string, request: DirectSaleRequest): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/fastQuote/management/directSale/tradeIn/${tradeInVehicleId}`, request)
      .pipe(map((response: DefaultStatus) => response));
  }

  getPotentialBuyers(fastQuoteId: string, filters?: PotentialBuyerFilters): Observable<PotentialBuyerResponse> {
    let params = new HttpParams();
    if (filters?.region) {
      params = params.set('region', filters.region);
    }
    if (filters?.modelFilter) {
      params = params.set('modelFilter', filters.modelFilter);
    }
    if (filters?.onlyBoughtFromSeller !== undefined) {
      params = params.set('onlyBoughtFromSeller', String(filters.onlyBoughtFromSeller));
    }
    if (filters?.periodDays !== undefined) {
      params = params.set('periodDays', String(filters.periodDays));
    }

    return this.http
      .get<PotentialBuyerResponse>(
        `${environment.api}/fastQuote/management/${fastQuoteId}/potential-buyers`,
        { params }
      )
      .pipe(map((response: PotentialBuyerResponse) => response));
  }
}
